import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";

export interface NavItem {
  path: string;
  label: string;
}

const MAX_RECENTS = 8;

interface NavState {
  recents: NavItem[];
  favorites: NavItem[];
  /** Record a visit to a page (deduped by path, newest first, capped). */
  visit: (item: NavItem) => void;
  /** Pin/unpin a page. */
  toggleFavorite: (item: NavItem) => void;
  isFavorite: (path: string) => boolean;
  removeRecent: (path: string) => void;
}

/**
 * Wayfinding state: recently-visited pages + user-pinned favorites. Surfaced in
 * the Cmd/K palette so it doubles as a launcher. Persisted to localStorage.
 * AWS-level UX plan, Phase 6.
 */
export const useNavStore = create<NavState>()(
  persist(
    (set, get) => ({
      recents: [],
      favorites: [],
      visit: (item) =>
        set((state) => {
          const rest = state.recents.filter((r) => r.path !== item.path);
          return { recents: [item, ...rest].slice(0, MAX_RECENTS) };
        }),
      toggleFavorite: (item) =>
        set((state) => {
          const exists = state.favorites.some((f) => f.path === item.path);
          return {
            favorites: exists
              ? state.favorites.filter((f) => f.path !== item.path)
              : [...state.favorites, item],
          };
        }),
      isFavorite: (path) => get().favorites.some((f) => f.path === path),
      removeRecent: (path) =>
        set((state) => ({
          recents: state.recents.filter((r) => r.path !== path),
        })),
    }),
    {
      name: "cloudpanel.nav",
      storage: createJSONStorage(() => localStorage),
    },
  ),
);
